import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Helmet from '../../components/Helmet';

import { FormattedMessage } from 'react-intl';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Stack, Container, Typography, CircularProgress } from '@mui/material';
import { Button, Icons } from '@classity-edtech/ui-base';

import ServiceTerms from '../../components/ServiceTerms';
import { Navigation } from '../../components/Enrolment';
import { webPaths } from '../../routes/paths';

import { useGetPageContentQuery } from '../../services/seoContent';
import { FreeFormName } from '@classity-edtech/platform-utils';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';

export interface OnboardStudentProps extends MuiBoxProps {
  sbNext?: () => void;
}

export const OnboardStudent = ({ className, sbNext }: OnboardStudentProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const screens = useAppMediaQuery();
  const [showServiceTerms, setShowServiceTerms] = useState(false);
  const { data, isLoading: fetchingContent } = useGetPageContentQuery({
    tag: 'onboardStudent',
    contentType: 'page',
  });

  const extractContent = () => {
    if (!!data) {
      return {
        seo: {
          title: data?.seo?.fields?.titleTag,
          keywords: data?.seo?.fields?.keywords,
          description: data?.seo?.fields?.metaDescription,
          metaRobotsIndex: data?.seo?.fields?.metaRobotsIndex,
        },
        header: data?.sections?.[0]?.fields?.title,
        description: data?.sections?.[0]?.fields?.description?.content?.[0]?.content?.[0]?.value,
        list: data?.sections?.[0]?.fields?.list?.map(item => {
          return {
            icon: item?.fields?.media?.fields?.icon?.fields?.file?.url,
            content: item?.fields?.title,
          };
        }),
        terms: data?.sections?.[1]?.fields?.description?.content?.[0]?.content?.[0]?.value,
        termsLink: {
          uri:
            data?.sections?.[1]?.fields?.description?.content?.[0]?.content?.[1]?.data?.uri || '',
          label:
            data?.sections?.[1]?.fields?.description?.content?.[0]?.content?.[1].content?.[0]
              ?.value,
        },
        cta: {
          label: data?.sections?.[1]?.fields?.list?.[0]?.fields?.reference?.fields?.label,
          url: data?.sections?.[1]?.fields?.list?.[0]?.fields?.reference?.fields?.url,
        },
      };
    }
  };

  const handleCloseModal = () => {
    navigate(location.pathname);
  };

  const handleCloseProcess = () => {
    return navigate('/');
  };

  const handleProceed = () => {
    navigate(
      `/${webPaths.studentEnrolment}?screen=1&formName=${FreeFormName.REGISTRATIONS_STUDENT}`,
    );
  };

  useEffect(() => {
    if (location.hash === '#terms') setShowServiceTerms(true);
    else setShowServiceTerms(false);
  }, [location]);

  return (
    <>
      {fetchingContent ? (
        <Box
          className={`${className} loader`}
          sx={{
            display: 'flex',
            width: '100vw',
            height: '100vh',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Helmet
            title={extractContent().seo?.title}
            keywords={extractContent().seo?.keywords}
            description={extractContent().seo?.description}
            metaRobotsIndex={extractContent().seo?.metaRobotsIndex}
          />
          {!showServiceTerms && (
            <Box className={`${className}`} sx={{ pb: { xs: '64px', md: '0' } }}>
              <Container>
                <Navigation close={handleCloseProcess} />
                <Stack
                  sx={{
                    justifyContent: 'center',
                    gap: { xs: 3, md: 4 },
                    py: { xs: 3, md: 4 },
                    px: { xs: 0, md: 6 },
                  }}
                >
                  <Stack sx={{ gap: 2.5 }}>
                    <Typography variant={screens.isDesktop ? 'h3' : 'h4'}>
                      {extractContent().header}
                    </Typography>
                  </Stack>
                  <Stack sx={{ gap: 4, maxWidth: '568px' }}>
                    <Typography
                      variant={screens.isDesktop ? 'subtitle2' : 'subtitle5'}
                      sx={{ mb: { xs: 1, md: 4 } }}
                    >
                      {extractContent().description}
                    </Typography>
                    {extractContent()?.list?.map((item, index) => (
                      <Typography
                        variant='body1'
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'centet',
                          gap: '10px',
                        }}
                      >
                        <img src={item.icon} alt='' />
                        {item.content}
                      </Typography>
                    ))}
                  </Stack>
                  <Typography variant='body2' sx={{ mt: { xs: 1, md: 4 } }}>
                    {extractContent()?.terms}{' '}
                    <a href={extractContent()?.termsLink?.uri}>
                      <Typography variant='caption1'>
                        {extractContent()?.termsLink?.label}
                      </Typography>
                    </a>
                  </Typography>

                  <Link
                    to={`/${webPaths.studentEnrolment}?screen=1&formName=${FreeFormName.REGISTRATIONS_STUDENT}`}
                    data-event='call_to_action_click'
                    data-traffic-type={
                      process.env.NODE_ENV === 'production' ? 'production' : 'local'
                    }
                    onClick={handleProceed}
                    style={{ textDecoration: 'none', width: '100%', marginTop: 'auto' }}
                  >
                    <Button
                      sx={{ width: { md: '400px' }, mt: '32px' }}
                      data-testid='start-enrolment'
                      showRightIcon
                    >
                      <Typography variant='button'>{extractContent()?.cta?.label}</Typography>
                    </Button>
                  </Link>
                </Stack>
              </Container>
            </Box>
          )}
          {showServiceTerms && (
            <Box className={className}>
              <Container>
                <Stack sx={{ gap: 3 }}>
                  <Box sx={{ position: 'relative' }}>
                    <Typography
                      variant='subtitle1'
                      className='terms__title'
                      sx={{ textTransform: 'uppercase', textAlign: 'center' }}
                    >
                      <FormattedMessage id='web.service.terms' />
                    </Typography>
                  </Box>
                  <Icons.Cancel
                    data-testid='close-service-terms'
                    sx={{
                      position: 'absolute',
                      right: { xs: '24px', md: '104px' },
                      top: '24px',
                      cursor: 'pointer',
                    }}
                    onClick={handleCloseModal}
                  />
                  <Box sx={{ maxHeight: 'calc(100vh - 256px)', overflowY: 'auto' }}>
                    <ServiceTerms />
                  </Box>

                  <Button sx={{ mx: 'auto', maxWidth: 'auto' }} onClick={handleProceed}>
                    <Typography variant='button'>
                      <FormattedMessage id='web.agree.and.continue' />
                    </Typography>
                  </Button>
                </Stack>
              </Container>
            </Box>
          )}
        </>
      )}
    </>
  );
};

OnboardStudent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

OnboardStudent.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(OnboardStudent)(
  ({ theme }) => `
  &.MuiBox-root {
  }
`,
);
